import { useNavigate } from 'react-router-dom';

export interface StatCardProps {
  title: string;
  value: string;
  icon?: string;
  trend?: {
    positive: boolean;
    value: string;
  };
  colorClass?: string;
  navigateTo?: string;
  detailLabel?: string;
  size?: 'sm' | 'md' | 'lg';
  hideIfZero?: boolean;
}

export default function StatCard({ title, value, icon, trend, colorClass, navigateTo, detailLabel = 'Detay', size = 'md', hideIfZero }: StatCardProps) {
  const navigate = useNavigate();

  if (hideIfZero && (value === '0' || value === '...')) return null;

  const sizeClasses = {
    sm: 'p-3 md:p-4',
    md: 'p-3 md:p-5',
    lg: 'p-4 md:p-6',
  };
  const iconSizes = {
    sm: 'w-7 h-7 md:w-8 md:h-8 rounded-lg',
    md: 'w-7 h-7 md:w-10 md:h-10 rounded-lg md:rounded-xl',
    lg: 'w-10 h-10 md:w-12 md:h-12 rounded-xl',
  };
  const iconFontSizes = {
    sm: 'text-sm md:text-base',
    md: 'text-sm md:text-lg',
    lg: 'text-lg md:text-2xl',
  };
  const valueSizes = {
    sm: 'text-base md:text-lg',
    md: 'text-lg md:text-2xl',
    lg: 'text-lg md:text-3xl',
  };
  const titleSizes = {
    sm: 'text-[10px] md:text-[11px]',
    md: 'text-[10px] md:text-xs',
    lg: 'text-[11px] md:text-xs',
  };

  const cardContent = (
    <>
      <div className="flex items-start justify-between mb-2 md:mb-3">
        <span className={`${titleSizes[size]} font-medium text-foreground-400 uppercase tracking-wider`}>{title}</span>
        {icon && colorClass && (
          <span className={`${iconSizes[size]} ${colorClass} flex items-center justify-center transition-transform duration-300 group-hover:scale-110`}>
            <i className={`${icon} ${iconFontSizes[size]} text-background-50`}></i>
          </span>
        )}
      </div>
      <p className={`${valueSizes[size]} font-bold text-foreground-900 mb-1 font-heading truncate`}>{value}</p>
      <div className="flex items-center justify-between">
        {trend ? (
          <div className="flex items-center gap-1.5">
            <span className={`text-xs font-medium ${trend.positive ? 'text-emerald-600' : 'text-red-500'}`}>
              <i className={`${trend.positive ? 'ri-arrow-up-line' : 'ri-arrow-down-line'} text-[10px]`}></i>
              {' '}{trend.value}
            </span>
            <span className="text-[11px] text-foreground-400">geçen aya göre</span>
          </div>
        ) : (
          <span className="text-[11px] text-foreground-400"></span>
        )}
        {navigateTo && (
          <span className="text-[11px] text-primary-500 font-medium flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            {detailLabel} <i className="ri-arrow-right-s-line text-xs"></i>
          </span>
        )}
      </div>
    </>
  );

  if (navigateTo) {
    return (
      <div
        className={`stat-card group ${sizeClasses[size]} cursor-pointer hover:border-primary-300/60 transition-all duration-200 hover:-translate-y-0.5`}
        onClick={() => navigate(navigateTo)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate(navigateTo); }}
      >
        {cardContent}
      </div>
    );
  }

  return (
    <div className={`stat-card group ${sizeClasses[size]}`}>
      {cardContent}
    </div>
  );
}